import { useEffect, useRef, useState, useCallback } from "react";
import { wsUrl } from "@/lib/api";

/**
 * Subscribes to /api/ws/alerts. Multiplexes channels:
 *   - "alert"          → new event
 *   - "event_update"   → existing event status change
 *   - "kpi"            → composite KPI tick
 *   - "hello" / "pong" → connection acks
 */
export const useLiveAlerts = ({ max = 60 } = {}) => {
  const [alerts, setAlerts] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [matchContext, setMatchContext] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  const connect = useCallback(() => {
    try {
      const ws = new WebSocket(wsUrl("/ws/alerts"));
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retryRef.current = 0;
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.channel === "alert") {
            setAlerts((prev) => [msg.data, ...prev].slice(0, max));
          } else if (msg.channel === "event_update") {
            setAlerts((prev) =>
              prev.map((a) => (a.id === msg.data.id ? { ...a, ...msg.data } : a))
            );
          } else if (msg.channel === "kpi") {
            setKpi(msg.data);
            if (msg.data?.match_context) setMatchContext(msg.data.match_context);
          }
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        const delay = Math.min(8000, 1000 * Math.pow(2, retryRef.current++));
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    } catch {
      setTimeout(connect, 2000);
    }
  }, [max]);

  useEffect(() => {
    connect();
    return () => { try { wsRef.current?.close(); } catch {} };
  }, [connect]);

  const updateAlertLocally = useCallback((id, patch) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  return { alerts, kpi, matchContext, connected, updateAlertLocally };
};
